<script setup lang="ts">
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import {
  actionCellRendererGenerator,
  selectionCellRendererGenerator,
  selectionHeaderCellRendererGenerator,
} from '@/components/common/element-plus/ElTableV2'
import TableSearchFilter from '@/components/common/TableSearchFilter.vue'
import RcSiteSelect, { type ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import RcWorkOrderSelect from '@/components/rc/common/RcWorkOrderSelect.vue'
import { sparePartsTableCellRendererGenerator } from '@/components/rc/spare-parts/table-render-helpers'
import { useSpareParts } from '@/composables/rc/spare-parts'
import { useRowInlineEdit, type ManagedRow } from '@/composables/table-row-inline-edit'
import { searchResources } from '@/services/core.service'
import { getSparePartsExportCSV } from '@/services/rc/spare-parts.service'
import { useOIDCStore } from '@/stores/oidc.store'
import type { ICustomerSearchResource } from '@/types/core/Customer'
import { SiteType } from '@/types/core/Site'
import type { ISparePartsDashboardFilters } from '@/types/rc/filters'
import { PartType } from '@/types/rc/miners/PartType'
import { SparePartStatus, type ISparePart } from '@/types/rc/spare-parts'
import { OrderBy } from '@/types/shared/filters'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { downloadFile } from '@/util'
import { Icon } from '@iconify/vue'
import { ElMessage } from 'element-plus'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types'
import { FixedDir } from 'element-plus/es/components/table-v2/src/constants.mjs'
import type { Column } from 'element-plus/es/components/table-v2/src/types.mjs'
import { cloneDeep } from 'lodash'
import { storeToRefs } from 'pinia'
import { computed, reactive, ref, watchEffect } from 'vue'
import SparePartHistoryDialog from '../spare-parts/SparePartHistoryDialog.vue'

const oidcStore = useOIDCStore()
const { isRcAppManagerUser, isRCAppUser, isCustomer } = storeToRefs(oidcStore)

const emit = defineEmits(['select', 'selectAll'])

// Spare part history dialog props
const isSparePartHistoryDialogOpen = ref<boolean>(false)
const selectedSparePartId = ref<number>()

const handleSparePartHistoryDialogOpen = (sparePart: ISparePart) => {
  isSparePartHistoryDialogOpen.value = true
  selectedSparePartId.value = sparePart.id
}

const handleSparePartHistoryDialogClose = () => {
  isSparePartHistoryDialogOpen.value = false
  selectedSparePartId.value = undefined
}

const sparePartStatusFilterOptions = ref<OptionType[]>([
  ...Object.values(SparePartStatus).map((status) => ({
    key: status,
    value: status,
  })),
  {
    key: 'In Stock (Refurbished)',
    value: 'In Stock (Refurbished)',
  },
])

const partTypeFilterOptions = ref<OptionType[]>([
  {
    key: PartType.ControlBoard,
    value: PartType.ControlBoard,
  },
  {
    key: PartType.Fan,
    value: PartType.Fan,
  },
  {
    key: PartType.Hashboard,
    value: PartType.Hashboard,
  },
  {
    key: PartType.PowerSupply,
    value: PartType.PowerSupply,
  },
])

const ownedByCompanyOwnerOption: OptionType = { label: 'Owned by Company', value: 0 }
const customerFilterOptions = ref<OptionType[]>([ownedByCompanyOwnerOption])
const customerFilterLoading = ref<boolean>(false)

const tableHeight = computed(() => 50 * (tableData.value.length + 1) + 8) // 50px per row + 1 for header + 8 for scrollbar

const isLoadingExport = ref<boolean>(false)

const filters = reactive<ISparePartsDashboardFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  order_by: 'id',
  order_by_order: OrderBy.DESC,
  part_type: undefined,
  status: undefined,
  customer_id: undefined,
  fragment: undefined,
  site: undefined,
  work_order_number: undefined,
  refurbished: undefined,
  archived: undefined,
})

const onSiteFilterUpdate = (value?: ISiteIdentifier) => {
  filters.site = value?.site_id ?? undefined
}

const onWorkOrderFilterUpdate = (value: { number?: string | undefined } | undefined) => {
  filters.work_order_number = value?.number
}

const filterSettings = [
  {
    key: 'type',
    value: 'Part Type',
    options: partTypeFilterOptions,
    type: 'dropdown',
    placeholder: 'Part Type',
    modelValue: ref<string>(),
    onChange: (val: string) => {
      filters.part_type = val
    },
    iconClass: 'build-outline',
    style: 'width: 224px',
  },
  {
    key: 'status',
    value: 'Status',
    options: sparePartStatusFilterOptions,
    type: 'dropdown',
    placeholder: 'Status',
    modelValue: ref<string>(),
    onChange: (val: string) => {
      if (val == '' || val === undefined) {
        filters.status = undefined
        filters.refurbished = undefined
      } else if (val === 'In Stock (Refurbished)') {
        filters.status = undefined
        filters.refurbished = true
      } else {
        filters.status = [val]
        filters.refurbished = undefined
      }
    },
    iconClass: 'label-outline-rounded',
    style: 'width: 224px',
  },
]

const searchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  placeholder: 'ID, SN or Model',
  style: 'width: 224px;',
  onChange: (val: string) => {
    if (val == '') {
      filters.fragment = undefined
    } else {
      filters.fragment = val
    }
  },
}

const { deleteSparesMutation, getSparesQuery, updateSparesMutation, refurbishSparesMutation } = useSpareParts()

// Setup listener to run on row updating
const onRowUpdate = async (row: ISparePart) => {
  if (!row.id) return
  // Update the part on the BE
  const data = await updateSparesMutation.mutateAsync({
    id: row.id,
    part_type: row.part_type,
    customer_id: row.customer.id,
    part_sn: row.part_sn ?? undefined,
    part_model: { name: row.part_model, version: row.part_model_version ?? null }!,
    price: row.price,
    site_id: row.site.site_id,
    status: row.status,
  })
  if (!tableData.value) return
  // Update the tableData model (with stable index)
  const updatedIndex = tableData.value.findIndex((part) => part.id === row.id)
  if (updatedIndex !== -1) {
    tableData.value[updatedIndex] = data
  }
}

// Model can be blanked out when changing type
const onSaveValidation = async (row: ISparePart) => {
  const valid = !!row.part_model
  if (!valid) ElMessage.warning('Please set a model')
  return valid
}

// BE query and table source data
const computedFilters = computed(() => filters)
const queryConfig = computed(() => ({ backgroundEnabled: !rowBeingEdited.value }))

const {
  isFetching,
  isError,
  isRefetching,
  data: queryData,
  error,
  refetch,
} = getSparesQuery(computedFilters, queryConfig)

const tableData = ref<ManagedRow<ISparePart>[]>([])
const selection = computed(() => tableData.value.filter((row) => row.selected))

watchEffect(() => {
  // Use `cloneDeep` to clone the proxied object, otherwise will get nested proxies
  tableData.value = cloneDeep(queryData.value?.data.map((sparePart) => ({ ...sparePart, selected: false }))) ?? []
})

const onUpdateFailedMessageTitle = 'Failed to update spare part'

// Inline-editable table
const { rowBeingEdited, onClickEdit } = useRowInlineEdit({ onRowUpdate, onUpdateFailedMessageTitle, onSaveValidation })

const columns: Column<ISparePart>[] = [
  {
    key: 'select',
    type: 'selection',
    width: 55,
    fixed: FixedDir.LEFT,
    cellRenderer: selectionCellRendererGenerator(() => emit('select', selection.value)),
    headerCellRenderer: selectionHeaderCellRendererGenerator(tableData, () => emit('selectAll', selection.value)),
    hidden: !isRCAppUser.value,
  },
  {
    title: 'ID',
    key: 'id',
    width: 64,
    fixed: FixedDir.LEFT,
    cellRenderer: sparePartsTableCellRendererGenerator(rowBeingEdited),
  },
  {
    title: 'Type',
    key: 'part_type',
    width: 175,
    cellRenderer: sparePartsTableCellRendererGenerator(rowBeingEdited),
  },
  {
    title: 'Model',
    key: 'part_model',
    width: 236,
    cellRenderer: sparePartsTableCellRendererGenerator(rowBeingEdited),
  },
  {
    title: 'SN',
    key: 'part_sn',
    width: 192,
    cellRenderer: sparePartsTableCellRendererGenerator(rowBeingEdited),
  },
  {
    title: 'Status',
    key: 'status',
    width: 120,
    cellRenderer: sparePartsTableCellRendererGenerator(rowBeingEdited),
  },
  {
    title: 'Used on',
    key: 'miner_used_on',
    width: 192,
    cellRenderer: sparePartsTableCellRendererGenerator(rowBeingEdited, { isCustomer: isCustomer.value }),
  },
  {
    title: 'Removed from',
    key: 'miner_coming_from',
    width: 192,
    cellRenderer: sparePartsTableCellRendererGenerator(rowBeingEdited, { isCustomer: isCustomer.value }),
  },
  {
    title: 'Work order',
    key: 'source_work_order',
    width: 192,
    cellRenderer: sparePartsTableCellRendererGenerator(rowBeingEdited),
  },
  {
    title: 'Price',
    key: 'price',
    width: 110,
    cellRenderer: sparePartsTableCellRendererGenerator(rowBeingEdited),
  },
  {
    title: 'Owner',
    key: 'customer',
    width: 288,
    cellRenderer: sparePartsTableCellRendererGenerator(rowBeingEdited),
    hidden: !isRCAppUser.value,
  },
  {
    title: 'Location',
    key: 'site',
    width: 180,
    cellRenderer: sparePartsTableCellRendererGenerator(rowBeingEdited),
  },
  {
    key: 'refurbish',
    width: 24,
    fixed: FixedDir.RIGHT,
    cellRenderer: sparePartsTableCellRendererGenerator(rowBeingEdited, { customMutation: refurbishSparesMutation }),
  },
  {
    key: 'history',
    width: 24,
    fixed: FixedDir.RIGHT,
    cellRenderer: actionCellRendererGenerator(
      'material-symbols:history',
      handleSparePartHistoryDialogOpen,
      rowBeingEdited,
      'History'
    ),
    hidden: !isRCAppUser.value,
  },
  {
    key: 'edit',
    width: 24,
    fixed: FixedDir.RIGHT,
    cellRenderer: actionCellRendererGenerator(
      { display: 'material-symbols:edit-rounded', editing: 'material-symbols:save-outline' },
      onClickEdit,
      rowBeingEdited,
      'Edit'
    ),
    hidden: !isRcAppManagerUser.value,
  },
  {
    key: 'delete',
    width: 32,
    fixed: FixedDir.RIGHT,
    cellRenderer: actionCellRendererGenerator('mdi:trash-can-outline', handleDelete, rowBeingEdited, 'Delete'),
    hidden: !isRcAppManagerUser.value,
  },
]

// Compute message to display when table is empty.
const tableEmptyText = computed(() => {
  if (isFetching.value && !filters.fragment) return 'Loading...'
  if (isFetching.value && filters.fragment) return `Searching for ${filters.fragment}`
  if (filters.fragment && !isFetching.value) return `No results for "${filters.fragment}"`
  else return 'Added parts will be displayed here'
})

// DELETE mutation
async function handleDelete(sparePart: ISparePart) {
  return DeleteConfirmationModal(
    'Delete Spare Part',
    'Are you sure you want to delete this spare part? This action cannot be undone',
    { id: sparePart.id },
    deleteSparesMutation.mutateAsync
  )
}

/* End ElTableV2 setup */

/**
 * Remote method for customer filter.
 *
 * @param query customer query fragment.
 */
const filterCustomers = async (query: string) => {
  if (query == '') return

  try {
    customerFilterLoading.value = true
    const filteredCustomers = await searchResources({
      resource: 'customer',
      fragment: query,
    })
    customerFilterOptions.value = filteredCustomers.map((res: ICustomerSearchResource) => {
      return {
        label: res.name,
        value: res.id,
      }
    })
    // Add "Owned by Company" option at the end
    customerFilterOptions.value.push(ownedByCompanyOwnerOption)
  } catch {
    ElMessage.error('Failed to filter customers')
    customerFilterOptions.value = []
  } finally {
    customerFilterLoading.value = false
  }
}

const handleExport = async () => {
  isLoadingExport.value = true
  try {
    downloadFile(await getSparePartsExportCSV(filters), `spare_parts_export.csv`)
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Unknown error occurred'
    ElMessage.error(message)
  } finally {
    isLoadingExport.value = false
  }
}

const handleArchivedFilterChange = (value: string | number | boolean) => {
  const archivedVal = value as boolean
  if (value) {
    filters.archived = archivedVal
  } else {
    filters.archived = undefined
  }
}
</script>

<template>
  <rl-error-result v-if="isError && !isRefetching" :error="error">
    <el-button @click="refetch()">
      <Icon icon="material-symbols:refresh-rounded" height="16" :inline="true" />&nbsp;Reload
    </el-button>
  </rl-error-result>
  <template v-else>
    <div class="spare-parts__controls">
      <div class="spare-parts__filters">
        <TableSearchFilter :search-settings="searchSettings" :disabled="isFetching" />
        <el-select-v2
          v-if="!isCustomer"
          v-model="filters.customer_id"
          :options="customerFilterOptions"
          style="width: 224px"
          filterable
          clearable
          remote
          :remote-method="filterCustomers"
          :loading="customerFilterLoading"
          :disabled="isFetching"
          placeholder="Owner"
          loading-text="Loading customers..."
        />
        <RcSiteSelect
          v-if="!isCustomer"
          placeholder="Site ID or Name"
          :type="SiteType.RepairCenter"
          :disabled="isFetching"
          style="width: 224px"
          @update:model-value="onSiteFilterUpdate"
        />
        <RcWorkOrderSelect
          v-if="!isCustomer"
          :disabled="isFetching"
          style="width: 224px"
          @update:model-value="onWorkOrderFilterUpdate"
        />
        <TableFilters :filters-list="filterSettings" />
        <el-checkbox v-if="!isCustomer" label="Archived" @change="handleArchivedFilterChange" />
      </div>
      <el-button v-if="!isCustomer" :loading="isLoadingExport" @click="handleExport">Export</el-button>
    </div>
    <el-auto-resizer :style="{ height: `${tableHeight}px` }">
      <template #default="{ width, height }">
        <el-table-v2 :columns="columns as any" :data="tableData" fixed :width="width" :height="height">
          <template #empty>
            <el-empty :description="tableEmptyText" />
          </template>
        </el-table-v2>
      </template>
    </el-auto-resizer>
    <el-pagination
      v-if="queryData && queryData.total_pages > 0"
      v-model:current-page="filters.page"
      v-model:page-size="filters.per_page"
      class="spare-parts__pagination"
      :page-sizes="DEFAULT_PAGE_SIZES"
      :page-count="queryData.total_pages"
      :total="queryData.total_records"
      :disabled="isFetching"
      :layout="DEFAULT_PAGINATION_LAYOUT"
      background
    />
    <SparePartHistoryDialog
      v-if="selectedSparePartId"
      v-model="isSparePartHistoryDialogOpen"
      :spare-part-id="selectedSparePartId"
      @close="handleSparePartHistoryDialogClose"
    />
  </template>
</template>

<style scoped lang="scss">
.spare-parts {
  &__controls {
    display: inline-flex;
    justify-content: space-between;
  }

  &__filters {
    display: flex;
    gap: var(--spacing-spacing-sm);
    flex-wrap: wrap;
  }

  &__pagination {
    align-self: flex-end;
  }
}

:deep() {
  .part-type-cell {
    display: flex;
    align-items: center;
    gap: var(--spacing-spacing-xs);
  }

  // Stripe the table out
  .el-table-v2__row:nth-child(even) {
    background-color: var(--fill-table-stripe);
  }

  // Hide the horizontal scrollbar
  .el-virtual-scrollbar.el-vl__horizontal {
    display: none;
  }
}
</style>
